function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SelectOption from './SelectOption';
import classNames from '../utils/classNames';
import Icon from '../Icon';
import ClickAway from '../ClickAway/ClickAway';
import keyCodes from '../FocusTrap/keyboardEventCodes';
/**
 * The Select component provides a stylable version of the select element.
 * It is used to select a value or set of values from a fixed list of options.
 * This component is based on the select-only combobox example provided by APG
 * and is more semantically correct and accessible than the current DropdownMenu component.
 * Currently, this component only support string for the text and value of options.
 *
 * - [APG Select-Only Combobox Example](https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/)
 *
 * ## Expected keyboard interactions:
 * - If focus is on combo-box (a.k.a the triggering part)
 *   - Pressing enter/spacebar should toggle the listbox (a.k.a the list of options)
 *   - Pressing up/down arrow should open the listbox
 * - If focus is on one of the options
 *   - Pressing enter/spacebar should select that option,close the listbox,
 *     and return focus to the combo-box
 *   - Pressing up/down arrow should cycle through the options on listbox
 * - If focus is on either the combo-box or one of the options
 *   - Pressing esc should close the listbox and returns focus to the combo-box
 *
 * ## Composed of
 * - [ClickAway](../ClickAway/README.md)
 *
 * ## Related Components
 * - [Option](./Option/README.md)
 *
 * ## Usage
 *
 * ```jsx
 * <Select
 *  ariaLabel="Select Edition"
 *  name="rateables"
 *  options={options}
 *  onOptionSelected={setSelectedValue}
 * />
 * ```
 *
 * ## Props
 */

var Select = function Select(props) {
  var ariaLabel = props.ariaLabel,
      className = props.className,
      isDisabled = props.isDisabled,
      name = props.name,
      onOptionSelected = props.onOptionSelected,
      onSelectExpanded = props.onSelectExpanded,
      options = props.options,
      qa = props.qa,
      selectedOptionIdx = props.selectedOptionIdx;
  var ref = /*#__PURE__*/React.createRef(null);

  var getValidSelectedOptionIdx = function getValidSelectedOptionIdx(idx, allOptions) {
    if (!allOptions.length || idx >= allOptions.length) {
      console.warn("Index ".concat(idx, " is out of bound! Returning 0 instead!"));
      return 0;
    }

    return idx;
  };

  var defaultSelectedName = '';
  var defaultSelectedIdx = -1;
  var defaultActiveDescendant = '';

  if (options === null || options === void 0 ? void 0 : options.length) {
    var _options$validSelecte;

    var validSelectedOptionIdx = getValidSelectedOptionIdx(selectedOptionIdx, options);
    defaultSelectedName = ((_options$validSelecte = options[validSelectedOptionIdx]) === null || _options$validSelecte === void 0 ? void 0 : _options$validSelecte.text) || '';
    defaultSelectedIdx = options[validSelectedOptionIdx] ? selectedOptionIdx : -1;
    defaultActiveDescendant = options[validSelectedOptionIdx] ? "".concat(name, "-").concat(selectedOptionIdx) : '';
  }

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isExpanded = _useState2[0],
      setIsExpanded = _useState2[1];

  var _useState3 = useState(defaultSelectedIdx),
      _useState4 = _slicedToArray(_useState3, 2),
      selectedIdx = _useState4[0],
      setSelectedIdx = _useState4[1];

  var _useState5 = useState(defaultSelectedName),
      _useState6 = _slicedToArray(_useState5, 2),
      selectedName = _useState6[0],
      setSelectedName = _useState6[1];

  var _useState7 = useState(defaultActiveDescendant),
      _useState8 = _slicedToArray(_useState7, 2),
      activedescendant = _useState8[0],
      setActiveDescendant = _useState8[1];

  var _useState9 = useState(defaultSelectedIdx),
      _useState10 = _slicedToArray(_useState9, 2),
      currentIdx = _useState10[0],
      setCurrentIdx = _useState10[1];

  var closeSelect = function closeSelect() {
    return setIsExpanded(false);
  };

  var onOption = function onOption(idx, text, value) {
    setSelectedName(text);
    setSelectedIdx(idx);
    setCurrentIdx(idx);
    setActiveDescendant("".concat(name, "-").concat(idx));

    if (onOptionSelected) {
      onOptionSelected(value);
    }

    closeSelect();

    if (ref === null || ref === void 0 ? void 0 : ref.current) {
      ref.current.focus();
    }
  };

  useEffect(function () {
    if (options === null || options === void 0 ? void 0 : options.length) {
      var _options$_validSelect, _options$_validSelect2;

      var _validSelectedOptionIdx = getValidSelectedOptionIdx(selectedOptionIdx, options);

      var newSelectedValue = ((_options$_validSelect = options[_validSelectedOptionIdx]) === null || _options$_validSelect === void 0 ? void 0 : _options$_validSelect.value) || '';
      var newSelectedName = ((_options$_validSelect2 = options[_validSelectedOptionIdx]) === null || _options$_validSelect2 === void 0 ? void 0 : _options$_validSelect2.text) || '';
      var newSelectedIdx = options[_validSelectedOptionIdx] ? _validSelectedOptionIdx : -1;
      var newActiveDescendant = options[_validSelectedOptionIdx] ? "".concat(name, "-").concat(_validSelectedOptionIdx) : '';
      setSelectedName(newSelectedName);
      setSelectedIdx(newSelectedIdx);
      setCurrentIdx(newSelectedIdx);
      setActiveDescendant(newActiveDescendant);

      if (onOptionSelected) {
        onOptionSelected(newSelectedValue);
      }
    }
  }, [selectedOptionIdx]);
  var listboxId = "listbox-".concat(name);

  var toggleSelect = function toggleSelect() {
    if (!isDisabled) {
      if (!isExpanded && onSelectExpanded) {
        onSelectExpanded();
      }

      setIsExpanded(!isExpanded);
    }
  };

  var handleKeydown = function handleKeydown(e) {
    if (isDisabled) return;
    var keyCode = e.keyCode;

    if (keyCode === keyCodes.ENTER || keyCode === keyCodes.SPACEBAR) {
      e.preventDefault();

      if (isExpanded) {
        onOption(currentIdx, options[currentIdx].text, options[currentIdx].value);
      } else {
        toggleSelect();
      }
    } else if (keyCode === keyCodes.ARROW_DOWN) {
      e.preventDefault();

      if (!isExpanded) {
        toggleSelect();
      } else if (currentIdx + 1 >= options.length) {
        setCurrentIdx(0);
      } else {
        setCurrentIdx(currentIdx + 1);
      }
    } else if (keyCode === keyCodes.ARROW_UP) {
      e.preventDefault();

      if (!isExpanded) {
        toggleSelect();
      } else if (currentIdx - 1 < 0) {
        setCurrentIdx(options.length - 1);
      } else {
        setCurrentIdx(currentIdx - 1);
      }
    } else if (keyCode === keyCodes.ESCAPE) {
      e.preventDefault();
      closeSelect();

      if (ref === null || ref === void 0 ? void 0 : ref.current) {
        ref.current.focus();
      }
    }
  };

  return /*#__PURE__*/React.createElement(ClickAway, {
    className: classNames('psw-fill-x'),
    onClickAway: closeSelect
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(className, 'psw-l-anchor', 'psw-l-stack-left', 'psw-utility-button')
  }, /*#__PURE__*/React.createElement("div", {
    "aria-disabled": isDisabled,
    "aria-expanded": isExpanded,
    "aria-haspopup": "listbox",
    "aria-label": ariaLabel,
    "aria-activedescendant": activedescendant,
    "aria-controls": listboxId,
    className: classNames({
      'psw-is-open': isExpanded
    }, {
      'psw-select-disabled': isDisabled
    }, 'psw-dropdown-trigger', 'psw-l-line-repel', 'psw-ally-indicator', 'psw-ally-indicator-gap-0', 'psw-ally-indicator-inset', 'psw-show-on-hover', 'psw-fill-x', 'psw-p-5', 'psw-p-y-3', 'psw-select', 'psw-t-bold', 'psw-t-link', 'psw-disable-nested-focus', 'psw-button'),
    "data-qa": "".concat(qa, "#combobox"),
    onClick: toggleSelect,
    onKeyDown: handleKeydown,
    ref: ref,
    role: "combobox",
    tabIndex: 0
  }, /*#__PURE__*/React.createElement("div", {
    className: "psw-t-truncate-1",
    "data-qa": "".concat(qa, "#combobox#selected-item")
  }, selectedName), /*#__PURE__*/React.createElement(Icon, {
    "data-qa": "".concat(qa, "#combobox#trigger"),
    type: "chevron-".concat(isExpanded ? 'up' : 'down'),
    size: "3"
  })), /*#__PURE__*/React.createElement("div", {
    className: classNames({
      'psw-hide': !isExpanded
    }, 'psw-c-bg-1', 'psw-clip', 'psw-l-z-15', 'psw-scroll-y', 'psw-l-stack-left', 'psw-fill-x', 'psw-s-2', 'psw-select-dropdown', 'psw-l-anchor-bottom-right', 'psw-l-top-right', 'psw-p-t-4'),
    "data-qa": "".concat(qa, "#listbox"),
    id: listboxId,
    role: "listbox"
  }, options.map(function (prop, idx) {
    return /*#__PURE__*/React.createElement(SelectOption, {
      descendantId: "".concat(name, "-").concat(idx),
      idx: idx,
      currentIdx: currentIdx,
      handleKeydown: handleKeydown,
      key: "".concat(name, "-").concat(prop.text),
      onOption: onOption,
      text: prop.text,
      value: prop.value,
      isSelected: idx === selectedIdx,
      qa: "".concat(qa, "#listbox"),
      telemetryMeta: prop.telemetryMeta,
      trackClick: prop.trackClick
    });
  }))));
};

Select.defaultProps = {
  ariaLabel: '',
  className: '',
  isDisabled: false,
  name: 'listbox',
  options: [],
  onOptionSelected: function onOptionSelected() {},
  onSelectExpanded: function onSelectExpanded() {},
  qa: 'select',
  selectedOptionIdx: 0
};
Select.propTypes = {
  /**
   * The label for this select component - used to identify the purpose of this select for
   * screen reader users
   */
  ariaLabel: PropTypes.string,

  /**
   * Allows adding a custom class or series of classes on the component
   * useful for setting margin or padding
   */
  className: PropTypes.string,

  /**
   * Whether the select should be disabled or not
   */
  isDisabled: PropTypes.bool,

  /**
   * Used to construct an id for the element that contains all options.
   */
  name: PropTypes.string,

  /**
   * The list of options to be added to select component.
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    value: PropTypes.string,
    telemetryMeta: PropTypes.string,
    trackClick: PropTypes.string
  })),

  /**
   * Callback to be called when one of the options is selected
   */
  onOptionSelected: PropTypes.func,

  /**
   * Callback to be called when select is toggled on
   */
  onSelectExpanded: PropTypes.func,

  /**
   * QA selector attribute used to target important elements for test validation
   */
  qa: PropTypes.string,

  /**
   * The initially selected option index
   */
  selectedOptionIdx: PropTypes.number
};
export default Select;